import { FC } from 'react';
import toast from 'react-hot-toast';

import {
  approveRequestJoinCompany,
  denyRequestJoinCompany,
  listRequestJoinCompany,
} from '@sdk/managers/UsersManager';
import { useAuth } from '@hooks/Auth';
import useSwr, { mutate } from 'swr';

const CompanyApprovalList: FC = () => {
  const { dbUser, company, loading } = useAuth();
  const fetchRequests = useSwr(
    !loading && company?.companyID ? ['/admin/approval', company.companyID] : null,
    async () => {
      if (!company) {
        throw Error('User does not own any company.');
      }

      const res: any = await listRequestJoinCompany(company.companyID, [
        'id',
        'email',
      ]);
      return res.data?.result?.items || [];
    },
    {
      revalidateOnFocus: true,
      onError: (err, key) => {
        console.log('Failed to fetch approvals', { key }, err);
        toast.error(err?.message || err?.errors?.[0]?.message);
      },
    }
  );

  const onToggleRequest = async (requestId: string, approved: boolean) => {
    try {
      if (!dbUser?.id || !dbUser?.username)
        throw new Error('Unexpected error.');

      if (!company) throw new Error('Failed to get company.');

      let action;
      if (approved) action = approveRequestJoinCompany;
      else action = denyRequestJoinCompany;

      await toast.promise(action(requestId), {
        loading: `${approved ? 'Approving' : 'Rejecting'} request...`,
        success: `Successfully ${approved ? 'approved' : 'rejected'} request`,
        error: (err) =>
          err.error ||
          err.message ||
          `Failed to ${approved ? 'approve' : 'reject'} request`,
      });

      await Promise.all([
        mutate(['/admin/approval', company?.companyID]),
        mutate(['/admin/members', company?.companyID])
      ])
    } catch (err: any) {
      console.log('Failed to remove company proxy', err);
    }
  };

  return (
    <>
      <div className="flex flex-col divide-opacity-30 divide-y-1 divide-solid ">
        {!(fetchRequests.data?.length > 0) && <div>No pending requests</div>}
        {fetchRequests.data?.map((req) => (
          <div
            key={`req-${req.id}`}
            className="flex items-center justify-between py-2"
          >
            <div>{req.user.email}</div>
            <div>
              <button
                className="px-2 hover:text-green"
                onClick={() => onToggleRequest(req.id, true)}
              >
                Approve
              </button>
              <button
                className="px-2 hover:text-red"
                onClick={() => onToggleRequest(req.id, false)}
              >
                Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default CompanyApprovalList;
